import React, { useEffect, useCallback, useRef, useState } from 'react';

function fullscreenSupported() {
  return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
}

function formatDate(item) {
  const raw = item.taken_at || item.created_at;
  if (!raw) return '';
  return new Date(raw).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const buttonStyle = { position: 'fixed', fontSize: 16, zIndex: 1001 };

export default function Lightbox({ items, index, onClose, onNavigate }) {
  const item = items[index];
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [actualSize, setActualSize] = useState(false);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight' && index < items.length - 1) onNavigate(index + 1);
    if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
  }, [index, items.length, onClose, onNavigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    function sync() {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    }
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  function toggleFullscreen(e) {
    e.stopPropagation();
    const el = containerRef.current;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }

  function handleClose() {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    }
    onClose();
  }

  if (!item) return null;

  return (
    <div
      ref={containerRef}
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, background: '#000', zIndex: 1000,
        overflow: actualSize ? 'auto' : 'hidden',
        display: actualSize ? 'block' : 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <button onClick={handleClose} style={{ ...buttonStyle, top: 16, right: 16 }} aria-label="Close">
        ×
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); setActualSize((v) => !v); }}
        style={{ ...buttonStyle, top: 16, right: 60 }}
        aria-label={actualSize ? 'Fit to screen' : 'View full size'}
        title={actualSize ? 'Fit to screen' : 'View full size (original resolution)'}
      >
        {actualSize ? '⊡' : '1:1'}
      </button>

      {fullscreenSupported() && (
        <button
          onClick={toggleFullscreen}
          style={{ ...buttonStyle, top: 16, right: 104 }}
          aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
        >
          {isFullscreen ? '⤢' : '⛶'}
        </button>
      )}

      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(index - 1); }}
          style={{ ...buttonStyle, left: 16, top: '50%', transform: 'translateY(-50%)' }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {index < items.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(index + 1); }}
          style={{ ...buttonStyle, right: 16, top: '50%', transform: 'translateY(-50%)' }}
          aria-label="Next"
        >
          ›
        </button>
      )}

      {actualSize ? (
        <img
          src={item.fullUrl}
          alt={item.description || ''}
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'block' }}
        />
      ) : (
        <>
          <img
            src={item.fullUrl}
            alt={item.description || ''}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: isFullscreen ? '95vh' : '80vh', objectFit: 'contain' }}
          />
          {!isFullscreen && (item.description || formatDate(item)) && (
            <div style={{ marginTop: 16, maxWidth: '80vw', textAlign: 'center' }}>
              {item.description && <p style={{ margin: 0, color: '#ccc' }}>{item.description}</p>}
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#777' }}>{formatDate(item)}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
